import React, { useEffect, useState } from "react";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from "@/components/ui/sheet";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { api, extractErr } from "@/lib/api";
import { toast } from "sonner";
import {
  Beaker, Layers, Factory, Truck as TruckIcon, Package, Calendar, ArrowRight,
  Hash, MapPin,
} from "lucide-react";

/**
 * Phase 10 — Tank traceability drawer.
 *
 * Shows for a given tank:
 *   - tank info
 *   - linked production orders (forward link)
 *   - lots used per material (with chain-of-custody to original goods intake)
 *   - all stock movements that reference this tank
 */
export function TankTraceabilitySheet({ tankId, onClose }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!tankId) return;
    setLoading(true);
    api
      .get(`/tanks/${tankId}/traceability`)
      .then(({ data }) => setData(data))
      .catch((e) => {
        toast.error(extractErr(e, "Failed to load traceability"));
        setData({ tank: null, orders: [], lots_used: [], movements: [] });
      })
      .finally(() => setLoading(false));
  }, [tankId]);

  const fmt = (v) => (v ? new Date(v).toLocaleString() : "—");

  return (
    <Sheet open={!!tankId} onOpenChange={(v) => !v && onClose()}>
      <SheetContent className="w-full sm:max-w-2xl overflow-y-auto" data-testid="tank-traceability-sheet">
        <SheetHeader>
          <SheetTitle className="flex items-center gap-2">
            <Beaker className="w-5 h-5 text-primary" /> Tank traceability
          </SheetTitle>
          <SheetDescription>
            Full lot-to-order traceability for this tank batch.
          </SheetDescription>
        </SheetHeader>

        {loading ? (
          <div className="mt-6 space-y-3">
            {[...Array(5)].map((_, i) => <Skeleton key={i} className="h-16" />)}
          </div>
        ) : !data?.tank ? (
          <div className="mt-6 text-sm text-muted-foreground">No data available.</div>
        ) : (
          <div className="mt-6 space-y-6">
            {/* Tank summary */}
            <section>
              <div className="rounded-2xl border border-border bg-card p-4">
                <div className="text-[11px] font-mono text-muted-foreground">{data.tank.batch_number}</div>
                <div className="font-heading font-semibold text-lg">{data.tank.name}</div>
                <div className="mt-2 flex flex-wrap gap-x-3 gap-y-1 text-xs text-muted-foreground">
                  <span className="inline-flex items-center gap-1"><Calendar className="w-3 h-3" />{fmt(data.tank.created_at)}</span>
                  {data.tank.size && <span>Target: <span className="font-mono">{data.tank.size}</span></span>}
                  {data.tank.expected_volume_l != null && <span>{data.tank.expected_volume_l} L</span>}
                  <span className="inline-flex items-center gap-1"><Layers className="w-3 h-3" />{(data.tank.materials || []).length} materials</span>
                  <span className="font-mono">€{Number(data.tank.total_cost || 0).toFixed(2)}</span>
                </div>
              </div>
            </section>

            {/* Linked production orders */}
            <section>
              <div className="flex items-center justify-between mb-2">
                <h3 className="text-sm font-semibold flex items-center gap-2">
                  <Factory className="w-4 h-4 text-primary" />
                  Linked production orders
                </h3>
                <Badge variant="secondary" data-testid="trace-orders-count">{data.orders_count}</Badge>
              </div>
              {data.orders_count === 0 ? (
                <div className="rounded-lg border border-dashed border-border p-4 text-xs text-muted-foreground text-center">
                  No production orders linked yet. Link this tank from a production order to start the forward trace.
                </div>
              ) : (
                <div className="space-y-2">
                  {data.orders.map((o) => (
                    <div key={o.id} className="rounded-lg border border-border bg-card p-3" data-testid={`trace-order-${o.id}`}>
                      <div className="flex items-center justify-between">
                        <div className="min-w-0">
                          <div className="text-[11px] font-mono text-muted-foreground">{o.order_number}</div>
                          <div className="font-medium truncate">{o.customer}</div>
                        </div>
                        <Badge variant="secondary" className="capitalize">{(o.status || "").replace(/_/g, " ")}</Badge>
                      </div>
                      <div className="mt-2 flex flex-wrap gap-x-3 gap-y-0.5 text-[11px] text-muted-foreground">
                        {o.production_date && <span className="inline-flex items-center gap-1"><Calendar className="w-3 h-3" />{new Date(o.production_date).toLocaleDateString()}</span>}
                        <span className="inline-flex items-center gap-1"><Package className="w-3 h-3" />{o.lines_count} line{o.lines_count === 1 ? "" : "s"}</span>
                        <span>Total qty: <span className="font-mono">{o.total_quantity}</span></span>
                        {o.completed_at && <span>Completed: {fmt(o.completed_at)}</span>}
                        {o.shipped_at && <span className="inline-flex items-center gap-1"><TruckIcon className="w-3 h-3" />Shipped: {fmt(o.shipped_at)}</span>}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </section>

            {/* Lots used */}
            <section>
              <div className="flex items-center justify-between mb-2">
                <h3 className="text-sm font-semibold flex items-center gap-2">
                  <Hash className="w-4 h-4 text-primary" />
                  Lots consumed
                </h3>
                <Badge variant="secondary" data-testid="trace-lots-count">{data.lots_count}</Badge>
              </div>
              {data.lots_count === 0 ? (
                <div className="rounded-lg border border-dashed border-border p-4 text-xs text-muted-foreground text-center">
                  No lots tracked on this tank. Use the lot picker when filling future tanks for full traceability.
                </div>
              ) : (
                <div className="rounded-lg border border-border overflow-hidden">
                  <table className="w-full text-sm">
                    <thead className="bg-muted/50 text-[11px] uppercase tracking-wide text-muted-foreground">
                      <tr>
                        <th className="text-left font-medium p-2">Material</th>
                        <th className="text-left font-medium p-2">Lot #</th>
                        <th className="text-right font-medium p-2">Used</th>
                        <th className="text-right font-medium p-2">Remaining</th>
                        <th className="text-left font-medium p-2">Intake</th>
                      </tr>
                    </thead>
                    <tbody>
                      {data.lots_used.map((l, i) => (
                        <tr key={i} className="border-t border-border" data-testid={`trace-lot-${i}`}>
                          <td className="p-2">
                            <div className="font-medium truncate">{l.item_name}</div>
                            <div className="font-mono text-[10px] text-muted-foreground">{l.item_sku}</div>
                          </td>
                          <td className="p-2 font-mono text-xs">{l.lot_number || "—"}</td>
                          <td className="p-2 text-right font-mono tabular-nums">{l.qty_consumed_in_tank}</td>
                          <td className="p-2 text-right font-mono tabular-nums">{l.lot_remaining}</td>
                          <td className="p-2 text-xs text-muted-foreground">
                            <div>{l.supplier || "—"}</div>
                            <div>{l.intake_date ? new Date(l.intake_date).toLocaleDateString() : "—"}</div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </section>

            {/* Movements */}
            <section>
              <div className="flex items-center justify-between mb-2">
                <h3 className="text-sm font-semibold flex items-center gap-2">
                  <ArrowRight className="w-4 h-4 text-primary" />
                  Stock movements
                </h3>
                <Badge variant="secondary">{(data.movements || []).length}</Badge>
              </div>
              {(data.movements || []).length === 0 ? (
                <div className="rounded-lg border border-dashed border-border p-4 text-xs text-muted-foreground text-center">
                  No movements recorded.
                </div>
              ) : (
                <div className="rounded-lg border border-border overflow-hidden">
                  <table className="w-full text-sm">
                    <thead className="bg-muted/50 text-[11px] uppercase tracking-wide text-muted-foreground">
                      <tr>
                        <th className="text-left font-medium p-2">When</th>
                        <th className="text-left font-medium p-2">Type</th>
                        <th className="text-left font-medium p-2">Item</th>
                        <th className="text-right font-medium p-2">Qty</th>
                        <th className="text-left font-medium p-2">Lot</th>
                      </tr>
                    </thead>
                    <tbody>
                      {data.movements.map((m) => (
                        <tr key={m.id} className="border-t border-border">
                          <td className="p-2 whitespace-nowrap text-xs">{new Date(m.timestamp).toLocaleString()}</td>
                          <td className="p-2"><span className="inline-flex rounded-full bg-muted text-muted-foreground px-2 py-0.5 text-[11px] capitalize">{(m.type || "").replace(/_/g, " ")}</span></td>
                          <td className="p-2"><div className="truncate max-w-[180px]">{m.item_name}</div><div className="font-mono text-[10px] text-muted-foreground">{m.item_sku}</div></td>
                          <td className="p-2 text-right font-mono tabular-nums">{m.quantity}</td>
                          <td className="p-2 font-mono text-xs">{m.lot_number || "—"}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </section>

            <div className="flex justify-end pt-2">
              <Button variant="outline" onClick={onClose} data-testid="trace-close">Close</Button>
            </div>
          </div>
        )}
      </SheetContent>
    </Sheet>
  );
}
